import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ApiService } from './api.service';

export interface MaintenanceStatus {
	isMaintenanceMode: boolean;
	maintenanceMessage?: string;
}

@Injectable({
	providedIn: 'root'
})
export class MaintenanceService {
	private apiService = inject(ApiService);
	private maintenanceStatus$ = new BehaviorSubject<MaintenanceStatus | null>(null);
	private cacheExpiry = 30000; // 30 secondi
	private lastFetchTime = 0;

	/**
	 * Ottiene lo stato di manutenzione (con cache)
	 */
	getMaintenanceStatus(forceRefresh: boolean = false): Observable<MaintenanceStatus> {
		const now = Date.now();
		const cachedStatus = this.maintenanceStatus$.value;

		// Se abbiamo un valore in cache e non è scaduto, restituiscilo
		if (!forceRefresh && cachedStatus && (now - this.lastFetchTime) < this.cacheExpiry) {
			return of(cachedStatus);
		}

		// Altrimenti, fai una nuova richiesta
		return this.apiService.get<MaintenanceStatus>('/settings/maintenance').pipe(
			tap(status => {
				this.maintenanceStatus$.next(status);
				this.lastFetchTime = now;
			}),
			catchError(error => {
				// In caso di errore, assumiamo che non ci sia manutenzione
				const defaultStatus: MaintenanceStatus = {
					isMaintenanceMode: false,
					maintenanceMessage: 'Il sito è temporaneamente in manutenzione. Torneremo presto!'
				};
				this.maintenanceStatus$.next(defaultStatus);
				return of(defaultStatus);
			})
		);
	}

	/**
	 * Ottiene lo stato corrente dalla cache (sincrono)
	 */
	getCurrentStatus(): MaintenanceStatus | null {
		return this.maintenanceStatus$.value;
	}

	/**
	 * Observable dello stato di manutenzione
	 */
	getMaintenanceStatus$(): Observable<MaintenanceStatus | null> {
		return this.maintenanceStatus$.asObservable();
	}

	/**
	 * Aggiorna lo stato di manutenzione (solo admin)
	 */
	updateMaintenanceStatus(isMaintenanceMode: boolean, maintenanceMessage?: string): Observable<MaintenanceStatus> {
		const updateDto: { isMaintenanceMode: boolean; maintenanceMessage?: string } = {
			isMaintenanceMode
		};
		if (maintenanceMessage !== undefined) {
			updateDto.maintenanceMessage = maintenanceMessage;
		}

		return this.apiService.put<MaintenanceStatus>('/settings/maintenance', updateDto).pipe(
			tap(status => {
				this.maintenanceStatus$.next(status);
				this.lastFetchTime = Date.now();
			})
		);
	}

	/**
	 * Invalida la cache e forza un refresh
	 */
	clearCache(): void {
		this.maintenanceStatus$.next(null);
		this.lastFetchTime = 0;
	}
}

